import React, { useState, useRef } from "react";
import { Download, Upload, FileText, Search, Filter, TrendingUp, TrendingDown, Package, Zap, AlertTriangle, CheckCircle, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// Mock data
const stockByMaterial = [
  { name: "Iron Ore", value: 2450, percentage: 35 },
  { name: "Coal", value: 1890, percentage: 27 },
  { name: "Limestone", value: 1620, percentage: 23 },
  { name: "Other", value: 940, percentage: 15 },
];

const stockBySite = [
  { name: "Main Site", value: 3500, capacity: 5000 },
  { name: "West Yard", value: 2100, capacity: 3000 },
  { name: "East Yard", value: 1300, capacity: 2000 },
];

const movements = [
  { date: "2025-11-27", inbound: 450, outbound: 380, net: 70 },
  { date: "2025-11-26", inbound: 380, outbound: 420, net: -40 },
  { date: "2025-11-25", inbound: 520, outbound: 350, net: 170 },
  { date: "2025-11-24", inbound: 290, outbound: 410, net: -120 },
  { date: "2025-11-23", inbound: 410, outbound: 280, net: 130 },
  { date: "2025-11-22", inbound: 360, outbound: 390, net: -30 },
  { date: "2025-11-21", inbound: 480, outbound: 320, net: 160 },
];

const materialMovements = [
  { material: "Iron Ore", inbound: 150, outbound: 120 },
  { material: "Coal", inbound: 180, outbound: 150 },
  { material: "Limestone", inbound: 90, outbound: 80 },
  { material: "Other", inbound: 30, outbound: 30 },
];

const COLORS = ["hsl(187,100%,42%)", "hsl(24.6,95%,53%)", "hsl(200,70%,60%)", "hsl(280,70%,60%)"];

export default function StockDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [materialFilter, setMaterialFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [selectedWidget, setSelectedWidget] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Calculate totals
  const totalStock = stockByMaterial.reduce((sum, item) => sum + item.value, 0);
  const totalCapacity = stockBySite.reduce((sum, item) => sum + item.capacity, 0);
  const usedCapacity = stockBySite.reduce((sum, item) => sum + item.value, 0);
  const capacityUtilization = Math.round((usedCapacity / totalCapacity) * 100);
  
  const today = movements[0];
  const yesterday = movements[1];
  const outboundVariance = ((today.outbound - yesterday.outbound) / yesterday.outbound) * 100;

  const handleExport = () => {
    const csvContent = [
      ["Material", "Current Stock (tons)", "Percentage"],
      ...stockByMaterial.map(item => [item.name, item.value, item.percentage + "%"]),
      [],
      ["Site", "Current Stock (tons)", "Capacity (tons)", "Utilization %"],
      ...stockBySite.map(item => [item.name, item.value, item.capacity, Math.round((item.value / item.capacity) * 100) + "%"])
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Stock_Report_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => {
    const template = [
      ["Material", "Inbound", "Outbound", "Notes"],
      ["Iron Ore", "450", "380", ""],
      ["Coal", "280", "250", ""],
      ["Limestone", "150", "100", ""],
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Stock_Import_Template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      alert(`File "${file.name}" uploaded successfully. Data would be processed here.`);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)] flex items-center gap-2">
            <Package className="w-8 h-8" />
            Stock Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Complete inventory overview and analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <Button 
            variant="outline" 
            size="sm" 
            onClick={triggerFileUpload}
            className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDownloadTemplate}
            className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
          >
            <Download className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExport}
            className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
          >
            <FileText className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card 
          className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)] cursor-pointer hover:bg-[hsl(215,28%,12%)] transition-colors" 
          onClick={() => { setSelectedWidget("stock"); setDetailsOpen(true); }}
          data-testid="widget-stock"
        >
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground mb-1">Current Stock</span>
              <span className="text-2xl font-bold text-[hsl(187,100%,42%)]">{totalStock.toLocaleString()} tons</span>
              <span className="text-xs text-muted-foreground mt-2">Total inventory</span>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)] cursor-pointer hover:bg-[hsl(215,28%,12%)] transition-colors" 
          onClick={() => { setSelectedWidget("capacity"); setDetailsOpen(true); }}
          data-testid="widget-capacity"
        >
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground mb-1">Capacity Utilization</span>
              <span className="text-2xl font-bold text-orange-500">{capacityUtilization}%</span>
              <div className="w-full bg-[hsl(215,28%,14%)] rounded-full h-2 mt-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full"
                  style={{ width: `${capacityUtilization}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)] cursor-pointer hover:bg-[hsl(215,28%,12%)] transition-colors" 
          onClick={() => { setSelectedWidget("inbound"); setDetailsOpen(true); }}
          data-testid="widget-inbound"
        >
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground mb-1">Today Inbound</span>
              <span className="text-2xl font-bold text-green-500">{today.inbound} tons</span>
              <span className="text-xs text-muted-foreground mt-2">Received today</span>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)] cursor-pointer hover:bg-[hsl(215,28%,12%)] transition-colors" 
          onClick={() => { setSelectedWidget("outbound"); setDetailsOpen(true); }}
          data-testid="widget-outbound"
        >
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground mb-1">Total Outbound</span>
              <span className="text-2xl font-bold text-blue-500">{today.outbound} tons</span>
              <div className="flex items-center gap-1 mt-2">
                {outboundVariance > 0 ? <TrendingUp className="w-3 h-3 text-red-500" /> : <TrendingDown className="w-3 h-3 text-green-500" />}
                <span className={`text-xs ${outboundVariance > 0 ? "text-red-500" : "text-green-500"}`}>
                  {Math.abs(outboundVariance).toFixed(1)}% vs yesterday
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)] cursor-pointer hover:bg-[hsl(215,28%,12%)] transition-colors" 
          onClick={() => { setSelectedWidget("variance"); setDetailsOpen(true); }}
          data-testid="widget-variance"
        >
          <CardContent className="pt-6">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground mb-1">Target Variance</span>
              <span className="text-2xl font-bold text-[hsl(187,100%,42%)]">{today.net > 0 ? "+" : ""}{today.net} tons</span>
              <span className="text-xs text-muted-foreground mt-2">Net movement today</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <CardHeader>
          <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(215,20%,45%)]" />
              <Input
                placeholder="Search materials or sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
              />
            </div>

            <Select value={materialFilter} onValueChange={setMaterialFilter}>
              <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                <SelectValue placeholder="Filter by Material" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Materials</SelectItem>
                {stockByMaterial.map(item => (
                  <SelectItem key={item.name} value={item.name}>{item.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                <SelectValue placeholder="Filter by Site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {stockBySite.map(item => (
                  <SelectItem key={item.name} value={item.name}>{item.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)] p-1 grid w-full grid-cols-4 h-auto">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] py-2"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="by-material"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] py-2"
          >
            By Material
          </TabsTrigger>
          <TabsTrigger
            value="by-site"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] py-2"
          >
            By Site
          </TabsTrigger>
          <TabsTrigger
            value="movements"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] py-2"
          >
            Movements
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Stock Distribution by Material</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stockByMaterial}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name} ${percentage}%`}
                      outerRadius={100}
                      fill="hsl(187,100%,42%)"
                      dataKey="value"
                    >
                      {stockByMaterial.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "hsl(215,28%,14%)", border: "1px solid hsl(215,28%,18%)" }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Capacity by Site</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stockBySite}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(215,28%,18%)" />
                    <XAxis stroke="hsl(215,20%,45%)" />
                    <YAxis stroke="hsl(215,20%,45%)" />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(215,28%,14%)", border: "1px solid hsl(215,28%,18%)" }} />
                    <Legend />
                    <Bar dataKey="value" fill="hsl(187,100%,42%)" name="Current Stock" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="capacity" fill="hsl(24.6,95%,53%)" name="Capacity" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">Stock Summary by Material</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stockByMaterial.map((item) => (
                  <div key={item.name} className="flex items-center justify-between p-3 bg-[hsl(215,28%,14%)] rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.value.toLocaleString()} tons ({item.percentage}%)</p>
                    </div>
                    <div className="w-32 bg-[hsl(215,28%,18%)] rounded-full h-2">
                      <div 
                        className="bg-[hsl(187,100%,42%)] h-2 rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Material Tab */}
        <TabsContent value="by-material" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Material Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stockByMaterial}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(215,28%,18%)" />
                    <XAxis stroke="hsl(215,20%,45%)" dataKey="name" />
                    <YAxis stroke="hsl(215,20%,45%)" />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(215,28%,14%)", border: "1px solid hsl(215,28%,18%)" }} />
                    <Bar dataKey="value" fill="hsl(187,100%,42%)" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Material Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stockByMaterial.map((item) => (
                    <div key={item.name} className="p-3 bg-[hsl(215,28%,14%)] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-white">{item.name}</p>
                        <Badge variant="outline" className="bg-[hsl(187,100%,42%)]/10 text-[hsl(187,100%,42%)] border-[hsl(187,100%,42%)]">
                          {item.percentage}%
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{item.value.toLocaleString()} tons in stock</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* By Site Tab */}
        <TabsContent value="by-site" className="space-y-6">
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">Site Utilization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {stockBySite.map((site) => {
                const utilization = Math.round((site.value / site.capacity) * 100);
                return (
                  <div key={site.name} className="p-4 bg-[hsl(215,28%,14%)] rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-white">{site.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {site.value.toLocaleString()} / {site.capacity.toLocaleString()} tons
                        </p>
                      </div>
                      <Badge 
                        variant="outline"
                        className={`${utilization > 80 ? "bg-red-500/10 text-red-500 border-red-500" : utilization > 60 ? "bg-orange-500/10 text-orange-500 border-orange-500" : "bg-green-500/10 text-green-500 border-green-500"}`}
                      >
                        {utilization}%
                      </Badge>
                    </div>
                    <div className="w-full bg-[hsl(215,28%,18%)] rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${utilization > 80 ? "bg-red-500" : utilization > 60 ? "bg-orange-500" : "bg-green-500"}`}
                        style={{ width: `${utilization}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements" className="space-y-6">
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">7-Day Inbound vs Outbound</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={movements}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(215,28%,18%)" />
                  <XAxis stroke="hsl(215,20%,45%)" />
                  <YAxis stroke="hsl(215,20%,45%)" />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(215,28%,14%)", border: "1px solid hsl(215,28%,18%)" }} />
                  <Legend />
                  <Line type="monotone" dataKey="inbound" stroke="hsl(200,100%,60%)" name="Inbound" strokeWidth={2} />
                  <Line type="monotone" dataKey="outbound" stroke="hsl(24.6,95%,53%)" name="Outbound" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">Material Movement Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={materialMovements}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(215,28%,18%)" />
                  <XAxis stroke="hsl(215,20%,45%)" dataKey="material" />
                  <YAxis stroke="hsl(215,20%,45%)" />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(215,28%,14%)", border: "1px solid hsl(215,28%,18%)" }} />
                  <Legend />
                  <Bar dataKey="inbound" fill="hsl(200,100%,60%)" name="Inbound" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="outbound" fill="hsl(24.6,95%,53%)" name="Outbound" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">Daily Movement Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-[hsl(215,28%,14%)] border-b border-[hsl(215,28%,18%)]">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Inbound</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Outbound</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Net Movement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(215,28%,18%)]">
                    {movements.map((movement) => (
                      <tr key={movement.date} className="hover:bg-[hsl(215,28%,14%)]">
                        <td className="px-3 py-2 text-sm text-white">{movement.date}</td>
                        <td className="px-3 py-2 text-sm text-green-500 font-medium">+{movement.inbound}</td>
                        <td className="px-3 py-2 text-sm text-orange-500 font-medium">-{movement.outbound}</td>
                        <td className="px-3 py-2 text-sm font-medium">
                          <span className={movement.net > 0 ? "text-green-500" : "text-red-500"}>
                            {movement.net > 0 ? "+" : ""}{movement.net}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">
              {selectedWidget === "stock" && "Current Stock Details"}
              {selectedWidget === "capacity" && "Capacity Utilization Details"}
              {selectedWidget === "inbound" && "Today Inbound Details"}
              {selectedWidget === "outbound" && "Total Outbound Details"}
              {selectedWidget === "variance" && "Target Variance Details"}
            </DialogTitle>
          </DialogHeader>

          {selectedWidget === "stock" && (
            <div className="space-y-4">
              <div className="p-4 bg-[hsl(215,28%,12%)] rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Total Current Stock</p>
                <p className="text-3xl font-bold text-[hsl(187,100%,42%)]">{totalStock.toLocaleString()} tons</p>
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">Breakdown by Material:</h4>
                {stockByMaterial.map(item => (
                  <div key={item.name} className="p-3 bg-[hsl(215,28%,12%)] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-white">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{item.value.toLocaleString()} tons</p>
                      </div>
                      <Badge className="bg-[hsl(187,100%,42%)]/20 text-[hsl(187,100%,42%)]">{item.percentage}%</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedWidget === "capacity" && (
            <div className="space-y-4">
              <div className="p-4 bg-[hsl(215,28%,12%)] rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Overall Utilization</p>
                <p className="text-3xl font-bold text-orange-500">{capacityUtilization}%</p>
                <div className="w-full bg-[hsl(215,28%,14%)] rounded-full h-3 mt-3">
                  <div className="bg-orange-500 h-3 rounded-full" style={{ width: `${capacityUtilization}%` }}></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{usedCapacity.toLocaleString()} / {totalCapacity.toLocaleString()} tons used</p>
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">By Site:</h4>
                {stockBySite.map(site => {
                  const util = Math.round((site.value / site.capacity) * 100);
                  return (
                    <div key={site.name} className="p-3 bg-[hsl(215,28%,12%)] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-white">{site.name}</p>
                          <p className="text-xs text-muted-foreground">{site.value.toLocaleString()} / {site.capacity.toLocaleString()} tons</p>
                        </div>
                        <Badge className={`${util > 80 ? "bg-red-500/20 text-red-500" : util > 60 ? "bg-orange-500/20 text-orange-500" : "bg-green-500/20 text-green-500"}`}>{util}%</Badge>
                      </div>
                      <div className="w-full bg-[hsl(215,28%,14%)] rounded-full h-2">
                        <div className={`h-2 rounded-full ${util > 80 ? "bg-red-500" : util > 60 ? "bg-orange-500" : "bg-green-500"}`} style={{ width: `${util}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {selectedWidget === "inbound" && (
            <div className="space-y-4">
              <div className="p-4 bg-[hsl(215,28%,12%)] rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Today Inbound</p>
                <p className="text-3xl font-bold text-green-500">{today.inbound} tons</p>
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">Last 7 Days Inbound:</h4>
                {movements.map(m => (
                  <div key={m.date} className="flex items-center justify-between p-3 bg-[hsl(215,28%,12%)] rounded-lg">
                    <span className="text-sm text-muted-foreground">{m.date}</span>
                    <span className="text-sm font-medium text-green-500">+{m.inbound} tons</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedWidget === "outbound" && (
            <div className="space-y-4">
              <div className="p-4 bg-[hsl(215,28%,12%)] rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Today Outbound</p>
                <p className="text-3xl font-bold text-blue-500">{today.outbound} tons</p>
                <p className="text-xs text-muted-foreground mt-2">Variance vs yesterday: {outboundVariance > 0 ? "+" : ""}{outboundVariance.toFixed(1)}%</p>
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">Last 7 Days Outbound:</h4>
                {movements.map(m => (
                  <div key={m.date} className="flex items-center justify-between p-3 bg-[hsl(215,28%,12%)] rounded-lg">
                    <span className="text-sm text-muted-foreground">{m.date}</span>
                    <span className="text-sm font-medium text-orange-500">-{m.outbound} tons</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedWidget === "variance" && (
            <div className="space-y-4">
              <div className="p-4 bg-[hsl(215,28%,12%)] rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Today Net Movement</p>
                <p className={`text-3xl font-bold ${today.net > 0 ? "text-green-500" : "text-red-500"}`}>{today.net > 0 ? "+" : ""}{today.net} tons</p>
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">Last 7 Days Net Movement:</h4>
                {movements.map(m => (
                  <div key={m.date} className="flex items-center justify-between p-3 bg-[hsl(215,28%,12%)] rounded-lg">
                    <span className="text-sm text-muted-foreground">{m.date}</span>
                    <span className={`text-sm font-medium ${m.net > 0 ? "text-green-500" : "text-red-500"}`}>{m.net > 0 ? "+" : ""}{m.net} tons</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
